import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { keyBy } from 'es-toolkit'
import { productionMethods, productionMethodsByCode } from '@/common'

const QUERY_KEY = 'app-config'

type CombustionFuelTypeDetinifiton = {
  emissionFactor: number
  emissionFactorLower?: number
  emissionFactorUpper?: number
  netCalorificValue: number | null
  source: string
  name: string
  id: string
}

export enum EmissionCalculationMethods {
  AluminiumPurificationSodaAsh = 'CO2.AL_PURIFICATION_SODA_ASH',
  AluminiumPurificationLimestone = 'CO2.AL_PURIFICATION_LIMESTONE',
  FlueGasCleaning = 'CO2.FLUE_GAS_CLEANING',
  Combustion = 'COMBUSTION',
  AluminiumPrebakeAnode = 'CO2.AL_PREBAKE_ANODE',
  AluminiumPrebakeSoderberg = 'CO2.AL_SODERBERG',
  PfcOvervoltage = 'PFC.OVERVOLTAGE',
  PfcSlope = 'PFC.SLOPE',
  CementClinkerInputBased = 'CO2.CEMENT_CLINKER_INPUT_BASED',
  CementClinkerOutputBased = 'CO2.CEMENT_CLINKER_OUTPUT_BASED',
  CementClinker = 'CO2.CEMENT_CLINKER'
}

export type AssetDefinition = {
  id: string
  aggregatedGoods: string[]
  emissionTypes: string[]
  nameEn: string
  name: string
  category: string
  calculationMethods: EmissionCalculationMethods[]
  productionMethods: string[]
}

type FuelTypeDefinition = {
  id: string
  name: string
  category: string
  el_2012_2015: number
  el_before_2012: number
  el_from_2016: number
  heat_direct_use_of_exhaust_gases_before_2016: number
  heat_direct_use_of_exhaust_gases_from_2016: number
  heat_hot_water_before_2016: number
  heat_hot_water_from_2016: number
  heat_steam_before_2016: number
  heat_steam_from_2016: number
}

type ProductionMethodDefinition = {
  code: string
  name: string
  aggregatedGoodsCategory: string
  cbamBenchmark: number
  precursorAggGoodCategory: string[]
  cnCodes: string[]
  usesMassBalance?: boolean
  isPrecursorRequired: boolean
  isApplicableForActivityLevelValidation?: boolean
}

type AppConfigResponse = {
  aggregatedGoodCategories: string[]
  assetDefinitions: AssetDefinition[]
  fuelTypeDefinitions: FuelTypeDefinition[]
  combustionFuelTypes: CombustionFuelTypeDetinifiton[]
}

type AppConfig = AppConfigResponse & {
  productionMethods: ProductionMethodDefinition[]
  assetsById: Record<string, AssetDefinition>
  assets: AssetDefinition[]
  productionMethodsByCode: Record<string, ProductionMethodDefinition>
  combustionFuelTypesById: Record<string, CombustionFuelTypeDetinifiton>
}

const initialState: AppConfig = {
  productionMethods: [],
  aggregatedGoodCategories: [],
  assets: [],
  assetDefinitions: [],
  assetsById: {},
  fuelTypeDefinitions: [],
  combustionFuelTypes: [],
  productionMethodsByCode: {},
  combustionFuelTypesById: {}
}

export const useAppConfig = () => {
  const query = useQuery<AppConfigResponse>({
    queryKey: [QUERY_KEY],
    enabled: false,
    queryFn: async () => {
      const res = await apiClient.get(`/v1/config`)
      return res.data
    },
    staleTime: Infinity
  })

  return {
    config: query.data
      ? {
          ...query.data,
          productionMethods,
          productionMethodsByCode,
          combustionFuelTypesById: keyBy(query.data.combustionFuelTypes, item => item.id),
          assetsById: keyBy(query.data.assetDefinitions, item => item.id)
        }
      : initialState,
    isLoaded: query.isSuccess,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    getConfig: query.refetch
  }
}

export enum Currency {
  CNY = 'CNY',
  EUR = 'EUR'
}

type CarbonPriceMechanism = {
  id: number
  name: string
  type: string
  currency: Currency
  status: string
  startDate: number
  scope: string
  jurisdiction: string
  coveredGasses: string[]
  areAllGassesCovered: boolean
}

export const useGetCarbonPriceMechanisms = () => {
  return useQuery<CarbonPriceMechanism[]>({
    queryKey: ['CARBON_PRICE_MECHIANSM'],
    queryFn: async () => {
      const res = await apiClient.get<CarbonPriceMechanism[]>('/v1/config/carbon-price-mechanisms')
      return res.data
    }
  })
}
